import APIRequest, { APIError } from "system/APIRequest";
import { ResourceTagEntry } from "./TagQueriesDialog";
import { Query } from "system/query/Query";
import { ApiPagination, Tag } from "./TagManagementPage";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";

export default class TagRequestHelper {

  static async create(name: string, description: string, color: string): Promise<string | null> {
    const body = {
      color,
      description,
      name
    };
    try {
      const response: any = await APIRequest("obj/resource-tag", {}, {
        method: "POST",
        body: JSON.stringify(body)
      }, true);
      return response.id;
    } catch (e) {
      const error = e as APIError;
      // TODO
    }
    return null;
  }

  static async update(id: string, name: string, description: string, color: string): Promise<boolean> {
    const body = {
      color,
      description,
      name
    };
    try {
      const response: any = await APIRequest(`obj/resource-tag/${id}`, {}, {
        method: "PUT",
        body: JSON.stringify(body)
      });
      return response;
    } catch (e) {
      const error = e as APIError;
      // TODO
    }
    return false;
  }

  static async delete(id: string): Promise<boolean> {
    try {
      const response: any = await APIRequest(`obj/resource-tag/${id}`, {}, {
        method: "DELETE"
      });
      return response;
    } catch (e) {
      const error = e as APIError;
      // TODO
    }
    return false;
  }

  static async getDashboardsForTag(tagId: string, pagination?: ApiPagination): Promise<{
    dashboards: Array<DashboardDocument>,
    pagination: ApiPagination,
    resourceTagEntries: Array<ResourceTagEntry>
  }> {
    const result = await this._getResourceTagEntriesForTag("dashboard", tagId, pagination);
    if (!result.resourceTagEntries.length) {
      return { dashboards: [], pagination: result.pagination, resourceTagEntries: [] };
    }
    
    const dashboards = await this._getDashboardsForResourceTagEntries(result.resourceTagEntries);
    return {
      dashboards: Array.isArray(dashboards) ? dashboards : [dashboards], 
      pagination: result.pagination,
      resourceTagEntries: result.resourceTagEntries
    };
  }

  static async getQueriesForTag(tagId: string, pagination?: ApiPagination): Promise<{
    pagination: ApiPagination,
    queries: Array<Query>,
    resourceTagEntries: Array<ResourceTagEntry>
  }> {
    const result = await this._getResourceTagEntriesForTag("query", tagId, pagination);
    if (!result.resourceTagEntries.length) {
      return { pagination: result.pagination, queries: [], resourceTagEntries: [] };
    }

    const queries = await this._getQueriesForResourceTagEntries(result.resourceTagEntries);
    return {
      pagination: result.pagination,
      queries: Array.isArray(queries) ? queries : [queries],
      resourceTagEntries: result.resourceTagEntries
    };
  }

  static async getTagsForQuery(queryId: string): Promise<{
    tags: Array<Tag>,
    resourceTagEntries: Array<ResourceTagEntry>
  }> {
    const queryParts = [{
      id: "resource_type",
      op: "=",
      value: "query"
    }, {
      id: "resource_id",
      op: "=",
      value: queryId
    }];
    const queryParams = {
      q: JSON.stringify(queryParts),
    };
    const response: any = await APIRequest("obj/resource-tag-entry", queryParams, undefined, true);
    const resourceTagEntries: Array<ResourceTagEntry> = response.output;

    // There are a few cases where `resourceTagEntries.length` threw an error due to `resourceTagEntries`
    // being undefined.  The API always returns an array with zero or more items so I'm not sure how that 
    // is possible here.  Since this request is made frequently, I'm adding a check to prevent any errors 
    // on that case.
    if (!resourceTagEntries || !resourceTagEntries.length) {
      return { tags: [], resourceTagEntries: []};
    }

    const tags = await this._getTagsForResourceTagEntries(resourceTagEntries);
    return {
      tags: Array.isArray(tags) ? tags : [tags],
      resourceTagEntries
    };
  }

  static async addTagToQuery(tagId: string, queryId: string): Promise<boolean> {
    const body = {
      resource_id: queryId,
      tag_id: tagId
    };
    try {
      const response = await APIRequest("tag-resource/query", {}, {
        body: JSON.stringify(body),
        method: "POST"
      }, true);
      return true;
    } catch (e) {
      const error = e as APIError;
      // TODO
      return false;
    }
  }

  static async removeTagFromQuery(tagId: string, queryId: string): Promise<boolean> {
    const resourceTagEntry = await this._getResourceTagEntryForQuery(tagId, queryId);
    if (!resourceTagEntry) {
      return false;
    }
    try {
      const response = await APIRequest(`obj/resource-tag-entry/${resourceTagEntry?.id}`, {}, {
        method: "DELETE"
      }, true);
      return true;
    } catch (e) {
      const error = e as APIError;
      // TODO
      return false;
    }
  }

  static async _getDashboardsForResourceTagEntries(entries: Array<ResourceTagEntry>): Promise<Array<DashboardDocument>> {
    const dashboardIds = entries.map((entry) => entry.resource_id);
    const dashboardIdsParam = dashboardIds.join(",");

    const response: any = await APIRequest(`obj/dashboard/${dashboardIdsParam}`, undefined, undefined, true);
    return Array.isArray(response.output) ? response.output : [response.output];
  }

  static async _getQueriesForResourceTagEntries(entries: Array<ResourceTagEntry>): Promise<Array<Query>> {
    const queryIds = entries.map((entry) => entry.resource_id);
    const queryIdsParam = queryIds.join(",");

    const response: any = await APIRequest(`obj/query/${queryIdsParam}`, undefined, undefined, true);
    return Array.isArray(response.output) ? response.output : [response.output];
  }

  static async _getResourceTagEntriesForTag(resourceType: "dashboard" | "query", tagId: string, pagination?: ApiPagination): Promise<{
    resourceTagEntries: Array<ResourceTagEntry>,
    pagination: ApiPagination
  }> {
    const queryParams: any = {
      q: JSON.stringify([{
        id: "resource_type",
        op: "=",
        value: resourceType
      }]),
      resource_tag_id: tagId
    };
    if (pagination?.page) {
      queryParams.page = pagination.page;
    }
    if (pagination?.per_page) {
      queryParams.per_page = pagination.per_page;
    }

    const response: any = await APIRequest("obj/resource-tag-entry", queryParams, undefined, true);
    return {
      resourceTagEntries: response.output,
      pagination: response.pagination
    };
  }

  static async _getResourceTagEntryForQuery(tagId: string, queryId?: string): Promise<ResourceTagEntry | null> {
    const filters = [{
      id: "resource_type",
      op: "=",
      value: "query"
    }];
    if (queryId) {
      filters.push({
        id: "resource_id",
        op: "=",
        value: queryId
      });
    }
    const queryParams = {
      q: JSON.stringify(filters),
      resource_tag_id: tagId
    };
    const response: any = await APIRequest("obj/resource-tag-entry", queryParams, undefined, true);
    return (response.output.length && response.output[0]) ?? null;
  }

  static async _getTagsForResourceTagEntries(entries: Array<ResourceTagEntry>): Promise<Array<Tag>> {
    const queryParams = {
      sort: "use_count",
      sort_order: "desc"
    };
    const tagIds = entries.map((entry) => entry.resource_tag_id);
    const tagIdsParam = tagIds.join(",");

    const response: any = await APIRequest(`obj/resource-tag/${tagIdsParam}`, queryParams, undefined, true);
    return response.output;
  }
}
